import CloseIcon from '@mui/icons-material/Close';
import { IconButton, LinearProgress, TextField } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { useSnackbar } from 'notistack';
import * as React from "react";
import { useState } from "react";
import Button from "../components/UI/Button";
import { ReservationStatus, RoomReservation, UserRole } from '../ostertalertypes';
import { useSettingsContext } from '../SettingsContext';
import { ViewType } from '../pages/PageMyBookings';
import { updateReservationStatus } from '../utils/database';
import { handleError } from '../utils/utils';

export type TDialogResult = number | null;
export type TButtonBar = { text: string, action: TDialogResult, required?: boolean }[];
export type TTextFieldProps = { disabled: boolean, value: string, label?: string }[]

/**
 * Snackbar text which is shown after a booking or cancel request was answered by the admin,
 * or after a guest requested the cancellation of a booking.
 * @param status the new status of the reservation
 * @returns the message or undefined if there is no message for this status
 */
function getResultMessage(status: ReservationStatus): string | undefined {
  switch (status) {
    case ReservationStatus.ACCEPTED:
      return "Buchungsanfrage angenommen";
    case ReservationStatus.BOOKING_REJECTED:
      return "Buchungsanfrage abgelehnt";
    case ReservationStatus.CANCELED:
      return "Stornierung angenommen, die Buchung wurde storniert";
    case ReservationStatus.CANCEL_REJECTED:
      return "Stornierungsanfrage abgelehnt";
    case ReservationStatus.CANCEL_REQUESTED: // guest requested the cancellation
      return "Stornierung angefragt";
    default:
      return undefined;
  }
}

export interface IDialogBookingComment {
  open: boolean;
  roomReservation: RoomReservation;
  viewType: ViewType;
  caption: string;
  textfields: TTextFieldProps;
  buttons: TButtonBar;
  onClose: (changed: boolean) => void;
}

export interface IDialogType {
  textfields: TTextFieldProps,
  buttons: TButtonBar,
  caption: string
}

export function DialogBookingComment(props: IDialogBookingComment) {

  const [comment, setComment] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const settingsCtx = useSettingsContext();

  /**
   * Is the member informed by email about the new status?
   * The server sends the email on every status change which was made by the admin, if the emails are enabled
   * in the settings and the member has an account with an email address (not for anonymous guests).
   * A cancel request of the guest does not send an email to the guest, but to the admin.
   * @param status the new status of the reservation
   */
  function isMemberNotified(status: ReservationStatus): boolean {
    return props.viewType !== "mine" &&
      status !== ReservationStatus.CANCEL_REQUESTED &&
      settingsCtx.settings?.reservation.emailOnReservationUpdate === true &&
      props.roomReservation.user?.role !== UserRole.ANONYM;
  }

  const onClose = async (action: TDialogResult) => {
    if (action != null) {
      try {
        setIsLoading(true);
        props.roomReservation.status = action;
        if (props.viewType === "mine") {
          props.roomReservation.userComment = comment;
        }
        else {
          props.roomReservation.adminComment = comment;
        }
        // normal users are only allowed to change their own comment, the server rejects admin comments from them
        await updateReservationStatus(props.roomReservation, props.viewType === "mine" ? ["status", "userComment"] : ["status", "adminComment"]);
        setIsLoading(false);
        let message = getResultMessage(action);
        if (message && isMemberNotified(action)) {
          message += " und E-Mail an Mitglied verschickt";
        }
        if (message) {
          enqueueSnackbar(message, {
            variant: "success",
            persist: false,
            anchorOrigin: { vertical: "top", horizontal: "center" },
          });
        }
      } catch (error) {
        handleError("Fehler beim Aktualisieren der Buchung", error, enqueueSnackbar, setIsLoading);
      }
    }
    props.onClose(action !== null ? true : false);
  };

  const onAbort = () => {
    props.onClose(false);
  };

  const onTextFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value.trim())
  }

  return (
    <Dialog onClose={onAbort} open={props.open} fullWidth>
      <LinearProgress style={{ "visibility": isLoading ? "visible" : "hidden" }} />
      <DialogTitle className={`flex content-center justify-between`}>
        {props.caption}
        <IconButton onClick={onAbort}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <div className="px-6 pb-6 rounded-lg shadow-lg bg-white">
        <TextField className="w-full mb-5"
          InputProps={{ inputProps: { min: 0, max: 20 } }}
          disabled={true}
          type="number"
          label="Anzahl Gäste"
          value={props.roomReservation.guestCount}
        />
        {props.textfields.map((tf, index) => {
          return <TextField key={index} label={tf.label} disabled={tf.disabled} id={"dialogttf" + index} className="w-full mb-5" onChange={onTextFieldChange} multiline rows={4} defaultValue={tf.value}
          />
        })}

        {props.buttons.length === 1 &&
          <Button className="w-full" onClick={() => onClose(props.buttons[0].action)} disabled={props.buttons[0].required ? comment.length === 0 : false}>
            {props.buttons[0].text}
          </Button>}

        {props.buttons.length === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-1 mt-6">
            <Button key="btn1" className="w-full" onClick={() => onClose(props.buttons[0].action)} disabled={props.buttons[0].required ? comment.length === 0 : false || isLoading}>
              {props.buttons[0].text}
            </Button>
            <Button key="btn2" className="w-full" onClick={() => onClose(props.buttons[1].action)} disabled={props.buttons[1].required ? comment.length === 0 : false || isLoading}>
              {props.buttons[1].text}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
